// Drag-snap geometry. Given the bboxes of the shapes being moved (with the
// drag delta already applied) and the bboxes of every other shape, finds
// the smallest delta correction that aligns one of the dragging edges or
// centers to a peer edge or center, within a screen-space threshold.
//
// Returns the correction and the world-space guide lines to render. The
// caller applies (dx + correction.dx, dy + correction.dy) and draws guides
// using vGuides (vertical lines at these world-x values) and hGuides
// (horizontal lines at these world-y values).
//
// Threshold is in screen pixels and divided by viewport scale before
// comparing world distances, so the snap window feels the same at any zoom.

export interface BBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface GuideSpan {
  // world-coord line position (x for vertical, y for horizontal)
  pos: number;
  // bounded range along the OTHER axis the line should span
  start: number;
  end: number;
}

export interface SnapResult {
  dx: number;
  dy: number;
  vGuides: GuideSpan[];
  hGuides: GuideSpan[];
}

interface AxisCandidate {
  // diff: signed correction needed to align (movingPos + diff === staticPos)
  diff: number;
  movingPos: number;
  staticPos: number;
  movingRange: { start: number; end: number };
  staticRange: { start: number; end: number };
}

function xAnchors(b: BBox): { left: number; center: number; right: number } {
  return { left: b.x, center: b.x + b.w / 2, right: b.x + b.w };
}

function yAnchors(b: BBox): { top: number; middle: number; bottom: number } {
  return { top: b.y, middle: b.y + b.h / 2, bottom: b.y + b.h };
}

function vSpan(a: BBox, b: BBox): { start: number; end: number } {
  return { start: Math.min(a.y, b.y), end: Math.max(a.y + a.h, b.y + b.h) };
}

function hSpan(a: BBox, b: BBox): { start: number; end: number } {
  return { start: Math.min(a.x, b.x), end: Math.max(a.x + a.w, b.x + b.w) };
}

export function computeSnap(
  moving: readonly BBox[],
  others: readonly BBox[],
  thresholdScreen: number,
  scale: number,
): SnapResult {
  if (moving.length === 0 || others.length === 0) {
    return { dx: 0, dy: 0, vGuides: [], hGuides: [] };
  }
  const thresholdWorld = thresholdScreen / Math.max(scale, 0.0001);

  let bestX: AxisCandidate | null = null;
  let bestY: AxisCandidate | null = null;

  for (const m of moving) {
    const mx = xAnchors(m);
    const my = yAnchors(m);
    for (const o of others) {
      const ox = xAnchors(o);
      const oy = yAnchors(o);

      // X-axis (vertical guide lines) — compare each moving X-anchor to each
      // other X-anchor. We only need 3×3 = 9 combinations per pair.
      for (const [, mp] of Object.entries(mx)) {
        for (const [, sp] of Object.entries(ox)) {
          const diff = sp - mp;
          if (Math.abs(diff) <= thresholdWorld) {
            if (!bestX || Math.abs(diff) < Math.abs(bestX.diff)) {
              bestX = {
                diff,
                movingPos: mp,
                staticPos: sp,
                movingRange: { start: m.y, end: m.y + m.h },
                staticRange: { start: o.y, end: o.y + o.h },
              };
            }
          }
        }
      }

      for (const [, mp] of Object.entries(my)) {
        for (const [, sp] of Object.entries(oy)) {
          const diff = sp - mp;
          if (Math.abs(diff) <= thresholdWorld) {
            if (!bestY || Math.abs(diff) < Math.abs(bestY.diff)) {
              bestY = {
                diff,
                movingPos: mp,
                staticPos: sp,
                movingRange: { start: m.x, end: m.x + m.w },
                staticRange: { start: o.x, end: o.x + o.w },
              };
            }
          }
        }
      }
    }
  }

  const result: SnapResult = { dx: 0, dy: 0, vGuides: [], hGuides: [] };
  if (bestX) {
    result.dx = bestX.diff;
    result.vGuides.push({
      pos: bestX.staticPos,
      start: Math.min(bestX.movingRange.start, bestX.staticRange.start),
      end: Math.max(bestX.movingRange.end, bestX.staticRange.end),
    });
  }
  if (bestY) {
    result.dy = bestY.diff;
    result.hGuides.push({
      pos: bestY.staticPos,
      start: Math.min(bestY.movingRange.start, bestY.staticRange.start),
      end: Math.max(bestY.movingRange.end, bestY.staticRange.end),
    });
  }
  return result;
}
